import React, { useMemo } from 'react';
import type { BiddingInfoResult } from '../../../types';
import { CaltivationCategoryOptions, ListingCategoryConst, ProductionHistoryOptions } from '../../../constants';
import { formatManufacturingMethod } from '../../../utils/biddingFieldFormat';
import dayjs from "dayjs";

// DBの文字列を受け取り、日本時間のミリ秒付き文字列に変換する
const formatJSTWithMillis = (dateStr: string | undefined | null) => {
  if (!dateStr) return "-";
  // .format() を通すことで、余計なミリ秒をカットし、区切り文字を整えます。
  return dayjs(dateStr).format("YYYY/MM/DD HH:mm:ss.SSS");
};

// # Props
interface BiddingMonitorItemDetailsProps {
  item: BiddingInfoResult;
  displayInfo: { totalCapacity: number };
}

// # 落札情報行詳細コンポーネント[管理者用]
export const BiddingMonitorItemDetails: React.FC<BiddingMonitorItemDetailsProps> = ({ item }) => {

  // # その他の定義
  // --- 入札データのソートロジック ---
  const sortedBids = useMemo(() => {
    if (!item.biddingInfoDatas) return [];

    // 元のデータを壊さないようにコピーしてからソート
    return [...item.biddingInfoDatas].sort((a, b) => {
      // 1. まず入札額で比較 (高い順)
      if (b.bidPrice !== a.bidPrice) {
        return b.bidPrice - a.bidPrice;
      }
      // 2. 入札額が同じなら、入札時間で比較 (早い順)
      const timeA = a.bidTimestamp || "";
      const timeB = b.bidTimestamp || "";
      if (timeA < timeB) return -1;
      if (timeA > timeB) return 1;
      return 0;
    });
  }, [item.biddingInfoDatas]);
  // 出品区分の判定
  const isRegularBidding = item.listingCategory === ListingCategoryConst.BIDDING;
  const isSale = item.listingCategory === ListingCategoryConst.SALE;

  return (
    <div className="bg-white">
      {/* --- 1. 出品詳細 (BiddingItemDetails と同様) --- */}
      <div className="p-4 border-t border-slate-200">
        <div className={`grid grid-cols-2 ${isSale ? "md:grid-cols-6" : "md:grid-cols-5"} gap-4`}>

          {/* GAP */}
          <div><p className="text-sm text-slate-500">GAP</p><p className="font-semibold">{item.gapName}</p></div>
          {/* 栽培区分 */}
          <div>
            <p className="text-sm text-slate-500">栽培区分</p>
            {(() => {
              const targetOption = CaltivationCategoryOptions.find(opt => opt.value === item.caltivationCategory);
              return targetOption ? (
                <p className="font-semibold">{targetOption.label}</p>
              ) : (
                <p className="font-semibold text-slate-400">-</p>
              );
            })()}
          </div>
          {/* 生産履歴 */}
          <div>
            <p className="text-sm text-slate-500">生産履歴</p>
            {(() => {
              const targetOption = ProductionHistoryOptions.find(opt => opt.value === item.productionHistory);
              return targetOption ? (
                <p className="font-semibold">{targetOption.label}</p>
              ) : (
                <p className="font-semibold text-slate-400">-</p>
              );
            })()}
          </div>
          {/* 製法区分（入札会専用・通常入札では非表示） */}
          {isSale && (
            <div>
              <p className="text-sm text-slate-500">製法区分</p>
              <p className="font-semibold">{formatManufacturingMethod(item.manufacturingMethod)}</p>
            </div>
          )}

          {/* 容量/本数 ① (0より大きい場合) */}
          {(Number(item.capacity1) > 0 && Number(item.quantity1) > 0) && (
              <div><p className="text-sm text-slate-500">容量/本数 ①</p><p className="font-semibold">{item.capacity1}kg × {item.quantity1}本</p></div>
          )}
          {/* 容量/本数 ② (0より大きい場合) */}
          {(Number(item.capacity2) > 0 && Number(item.quantity2) > 0) && (
              <div><p className="text-sm text-slate-500">容量/本数 ②</p><p className="font-semibold">{item.capacity2}kg × {item.quantity2}本</p></div>
          )}
        </div>
      </div>

      {/* --- 2. 全入札データ一覧テーブル --- */}
      <div className="p-4 border-t border-slate-200">
        <h4 className="text-base font-semibold text-slate-800 mb-4">全入札履歴</h4>
        {(item.biddingInfoDatas?.length > 0) ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">順位</th>
                  <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">買手</th>
                  <th scope="col" className="px-4 py-2 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">入札値 (円)</th>
                  {!isRegularBidding && (
                    <>
                        <th scope="col" className="px-4 py-2 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">総量 (kg)</th>
                        <th scope="col" className="px-4 py-2 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">入札数①</th>
                        <th scope="col" className="px-4 py-2 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">入札数②</th>
                    </>
                  )}
                    <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">入札日時</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {sortedBids.map((bid, index) => {
                    // 総量を計算
                    const totalBidCapacity = 
                        (Number(bid.quantity1) * Number(item.capacity1)) + 
                        (Number(bid.quantity2) * Number(item.capacity2));
                    return (
                        <tr key={bid.buyerCode} className={bid.buyerCode === item.highestBidBuyerCode ? 'bg-green-50' : ''}>
                            <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-slate-900">{index + 1}</td>
                            <td className="px-4 py-2 whitespace-nowrap text-sm text-slate-900">{bid.buyerName}</td>
                            <td className="px-4 py-2 whitespace-nowrap text-sm text-slate-900 text-right font-semibold font-mono">{bid.bidPrice.toLocaleString()}</td>
                            {!isRegularBidding && (
                                <>
                                    <td className="px-4 py-2 whitespace-nowrap text-sm text-slate-500 text-right">{totalBidCapacity}</td>
                                    <td className="px-4 py-2 whitespace-nowrap text-sm text-slate-500 text-right">{bid.quantity1}</td>
                                    <td className="px-4 py-2 whitespace-nowrap text-sm text-slate-500 text-right">{bid.quantity2}</td>
                                </>
                            )}
                            <td className="px-4 py-2 whitespace-nowrap text-xs text-slate-500 font-mono">
                              {formatJSTWithMillis(bid.bidTimestamp)}
                            </td>                      
                        </tr>
                    )
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-slate-500">この出品に対する入札データはありません。</p>
        )}
      </div>
      
    </div>
  );
};